import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { TeamRole } from '../../../../../../typings/TeamMember.interface';
import { withStaticProperties } from '../../../utils/withStaticProperties';
import { usePermissionAwareDisabled } from '../../contexts/TeamPermissionContext';
import StyledEllipsisOverflow from '../helpers/EllipsisOverflow';
import { getStyledUtils, StyledUtilsProps } from '../helpers/StyledUtils';
import Icon, { IconName } from './Icon';
import Text from './Text';

interface Props extends StyledUtilsProps {
  label: React.ReactNode;
  label_icon?: IconName;
  title?: string;
  icon?: 'add' | 'close';
  mono?: boolean;
  outline?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  to?: string | Partial<Location> | ((location: Location) => Partial<Location> | string);
}

const StyledChip = styled.button<Omit<Props, 'label' | 'label_icon' | 'icon' | 'onClick'>>(
  ({ theme, outline, mono }) => css`
    ${getStyledUtils}
    height: ${theme.pxToRem(20)};
    position: relative;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    font-size: ${theme.pxToRem(theme.font_sizes.xs.font_size)};
    line-height: ${theme.pxToRem(theme.font_sizes.xs.line_height)};
    background-color: ${outline
      ? theme.colors.surface.base.surface
      : theme.colors.surface.container.primary};
    border-radius: ${theme.radius.small};
    border: none;
    padding: 0;
    cursor: pointer;
    width: fit-content;
    max-width: 100%;
    overflow: hidden;

    &:disabled {
      background-color: ${theme.colors.surface.base.disabled};
      border-color: ${theme.colors.outline.neutral_2};
      color: ${theme.colors.on.neutral.secondary_neutral};
      cursor: not-allowed;

      &::before {
        border-color: ${theme.colors.outline.neutral_2};
      }

      span,
      div {
        color: ${theme.colors.on.neutral.secondary_neutral} !important;
        fill: ${theme.colors.on.neutral.secondary_neutral};
      }
    }

    ${mono &&
    css`
      font-family:
        JetBrains Mono,
        monospace;
      font-size: ${theme.pxToRem(theme.font_sizes.xs.font_size)};
      line-height: ${theme.pxToRem(theme.font_sizes.xs.line_height)};
      font-weight: ${theme.font_weights.medium};
    `}

    a, a > div {
      text-decoration: none;
      max-width: 100%;
      font-size: ${theme.pxToRem(theme.font_sizes[mono ? 'xs' : 's'].font_size)};
      line-height: ${theme.pxToRem(theme.font_sizes[mono ? 'xs' : 's'].line_height)};
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: ${outline
        ? `1px solid ${theme.colors.outline.neutral}`
        : `1px solid ${theme.colors.outline.primary}`};
      border-radius: ${theme.radius.small};
      pointer-events: none;
    }

    span:not(:first-child) {
      border-left: ${outline
        ? `1px solid ${theme.colors.outline.neutral}`
        : `1px solid ${theme.colors.outline.primary}`};
    }

    &:disabled {
      span:not(:first-child) {
        border-left: 1px solid ${theme.colors.outline.neutral_2};
      }
    }

    div {
      white-space: nowrap;
      color: ${outline
        ? theme.colors.on.neutral.primary_neutral
        : theme.colors.on.hue_container.primary};
    }

    > span:last-child {
      color: ${outline
        ? theme.colors.on.neutral.secondary_neutral
        : theme.colors.on.hue_container.primary};
    }

    &:hover:not(:disabled) {
      background-color: ${outline
        ? theme.colors.surface.base.hover.neutral
        : theme.colors.surface.container.hover.primary};
      &::before {
        border-color: ${outline
          ? theme.colors.outline.hover.neutral
          : theme.colors.outline.hover.container_primary};
      }
      span:not(:first-child) {
        border-color: ${outline
          ? theme.colors.outline.hover.neutral
          : theme.colors.outline.hover.container_primary};
      }
    }

    &:active {
      background-color: ${outline
        ? theme.colors.surface.base.active.neutral
        : theme.colors.surface.container.active.primary};
      &::before {
        border-color: ${outline
          ? theme.colors.outline.active.neutral
          : theme.colors.outline.active.container_primary};
      }
      div:not(:first-child) {
        border-color: ${outline
          ? theme.colors.outline.active.neutral
          : theme.colors.outline.active.container_primary};
      }
    }

    a:focus-visible,
    &:focus-visible {
      outline: 2px solid ${theme.colors.outline.focus.primary};
      outline-offset: 2px;
    }
  `,
);

const ChipComponent: React.FC<Props> = ({
  outline,
  onClick,
  label_icon,
  label,
  icon,
  to,
  title,
  ...props
}) => {
  const content = (
    <>
      <Text size="xs" title={title} semi ellipsis p={{ x: 1 }} flex={{ align: 'center' }}>
        {label_icon && (
          <Icon icon={label_icon} left={1} pointer primary={!outline} muted={outline} />
        )}
        <StyledEllipsisOverflow>{label}</StyledEllipsisOverflow>
      </Text>
      {icon && <Icon icon={icon} p={0.5} pointer />}
    </>
  );
  if (to) {
    return (
      <StyledChip outline={outline} {...props} as={'div' as any}>
        <Link to={to} onClick={(e) => e.stopPropagation()}>
          {content}
        </Link>
      </StyledChip>
    );
  }
  return (
    <StyledChip type="button" onClick={onClick} outline={outline} {...props}>
      {content}
    </StyledChip>
  );
};

const ChipPermission: React.FC<
  React.ComponentPropsWithRef<typeof ChipComponent> & { role?: TeamRole }
> = ({ role, ...clickableAreaProps }) => {
  const disabled = usePermissionAwareDisabled(clickableAreaProps.disabled, role);

  return <ChipComponent {...clickableAreaProps} disabled={disabled} />;
};

export default withStaticProperties(ChipComponent, { Permission: ChipPermission });
